import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/TailwindComponents/base/Button/Button';
import { Checkbox } from '@/components/TailwindComponents/base/Checkbox/Checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/TailwindComponents/base/Dialog/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/TailwindComponents/base/Select/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/TailwindComponents/base/Table/Table';
import { stage } from '@/config';
import {
  BarcodeScannerDelivery,
  SERIAL_BAUD_RATES,
  useBarcodeScanner,
} from '@/hooks';
import {
  FIELD_DISPLAY_NAMES,
  IDCardParseResult,
  parseIDCard,
  ParsedIDCard,
} from '@/utils/idCardParser';

export interface ScanIDModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (
    selectedFields: Partial<ParsedIDCard>,
    allParsedData: ParsedIDCard,
  ) => void;
  currentValues?: Partial<ParsedIDCard>;
}

type ScanState = 'idle' | 'scanning' | 'scanned' | 'error';

interface FieldSelection {
  field: keyof ParsedIDCard;
  selected: boolean;
  scannedValue: string;
  currentValue: string;
}

// Sample NY State ID barcode for testing (AAMVA format)
const MOCK_NY_ID_SCAN = `@

ANSI 636001090002DL00410288ZN03040015DL
DAQ123456789
DCF2ABCD3EFGH
DCSSTRASSMAN
DACDOMINIK
DADANY
DCUJR
DBB07151995
DBC2
DAYHAZ
DAU509
DAG123 MAIN ST # 212
DAIALBANY
DAJNY
DAK122030000
DCGUSA
DBJ987654321
DBA01012031
DBD01012023
DDB01012031
DCK1A2B3C4D5E
DCAD
DCBB
DCDP
`;

const DISPLAY_FIELDS: (keyof ParsedIDCard)[] = [
  'lastName',
  'firstName',
  'middleName',
  'dateOfBirth',
  'sex',
  'eyeColor',
  'height',
  'addressNumber',
  'streetAddress1',
  'streetAddress2',
  'city',
  'addressState',
  'zipCode',
  'country',
  'state',
  'idNumber',
  'vehicleClass',
  'restrictionCodes',
  'endorsementCodes',
  'expirationDate',
  'documentNumber',
  'issueDate',
];

const CUSTOM_DISPLAY_NAMES: Partial<Record<keyof ParsedIDCard, string>> = {
  vehicleClass: 'Class',
};

export const ScanIDModal: FC<ScanIDModalProps> = ({
  open,
  onClose,
  onApply,
  currentValues = {},
}) => {
  const [scanState, setScanState] = useState<ScanState>('idle');
  const [parseResult, setParseResult] = useState<IDCardParseResult | null>(
    null,
  );
  const [fieldSelections, setFieldSelections] = useState<FieldSelection[]>([]);
  const [selectAll, setSelectAll] = useState(true);
  const [lastDeliveryTransport, setLastDeliveryTransport] = useState<
    BarcodeScannerDelivery['transport'] | null
  >(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scanBufferRef = useRef<string>('');
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScanRef = useRef<{ value: string; at: number } | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setScanState('scanning');
      setParseResult(null);
      setFieldSelections([]);
      setSelectAll(true);
      scanBufferRef.current = '';
      lastScanRef.current = null;
      setLastDeliveryTransport(null);

      // Focus the hidden input after a short delay
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Process the scanned data
  const processScan = useCallback(
    (rawData: string) => {
      const result = parseIDCard(rawData);
      setParseResult(result);

      if (result.success && result.data) {
        const parsedData = result.data;
        setScanState('scanned');

        // Build field selections with current values comparison
        const selections: FieldSelection[] = DISPLAY_FIELDS.filter((field) => {
          const scannedValue = parsedData[field];
          return scannedValue && scannedValue.trim() !== '';
        }).map((field) => ({
          field,
          selected: true,
          scannedValue: parsedData[field],
          currentValue: (currentValues[field] as string) || '',
        }));

        setFieldSelections(selections);
      } else {
        setScanState('error');
      }
    },
    [currentValues],
  );

  // Shared entrypoint for both transports (keyboard wedge and HID POS).
  // Skips a payload identical to the one just processed so a hybrid device
  // emitting both HID reports and keystrokes cannot double-apply a scan.
  const submitScan = useCallback(
    (rawData: string) => {
      const now = Date.now();
      const last = lastScanRef.current;
      if (last && last.value === rawData && now - last.at < 500) {
        return;
      }
      lastScanRef.current = { value: rawData, at: now };
      processScan(rawData);
    },
    [processScan],
  );

  const handleScannerBarcode = useCallback(
    (delivery: BarcodeScannerDelivery) => {
      // Drop any pending wedge buffer so it cannot re-fire the same payload.
      scanBufferRef.current = '';
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      setLastDeliveryTransport(delivery.transport);
      submitScan(delivery.value);
    },
    [submitScan],
  );

  const {
    isHidSupported,
    isSerialSupported,
    status: scannerStatus,
    connectHid,
    connectSerial,
    serialBaudRate,
    setSerialBaudRate,
  } = useBarcodeScanner({ enabled: open, onBarcode: handleScannerBarcode });

  const handleConnectSerialScanner = useCallback(async () => {
    await connectSerial();
    // The chooser steals focus; the wedge fallback needs the textarea focused.
    inputRef.current?.focus();
  }, [connectSerial]);

  const handleConnectHidScanner = useCallback(async () => {
    await connectHid();
    inputRef.current?.focus();
  }, [connectHid]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      scanBufferRef.current = value;

      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }

      scanTimeoutRef.current = setTimeout(() => {
        if (scanBufferRef.current.length > 10) {
          setLastDeliveryTransport(null);
          submitScan(scanBufferRef.current);
        }
      }, 300);
    },
    [submitScan],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && scanBufferRef.current.length > 10) {
        e.preventDefault();
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }
        setLastDeliveryTransport(null);
        submitScan(scanBufferRef.current);
      }
    },
    [submitScan],
  );

  // Handle select all toggle
  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectAll(checked);
    setFieldSelections((prev) =>
      prev.map((selection) => ({ ...selection, selected: checked })),
    );
  }, []);

  // Handle individual field toggle
  const handleFieldToggle = useCallback(
    (field: keyof ParsedIDCard, checked: boolean) => {
      setFieldSelections((prev) => {
        const updated = prev.map((selection) =>
          selection.field === field
            ? { ...selection, selected: checked }
            : selection,
        );
        // Update selectAll based on all selections
        const allSelected = updated.every((s) => s.selected);
        setSelectAll(allSelected);
        return updated;
      });
    },
    [],
  );

  // Handle Apply button
  const handleApply = useCallback(() => {
    if (!parseResult?.data) return;

    const selectedData: Partial<ParsedIDCard> = {};
    fieldSelections
      .filter((s) => s.selected)
      .forEach((s) => {
        selectedData[s.field] = s.scannedValue;
      });

    onApply(selectedData, parseResult.data);
  }, [fieldSelections, parseResult, onApply]);

  // Handle retry scan
  const handleRetry = useCallback(() => {
    setScanState('scanning');
    setParseResult(null);
    setLastDeliveryTransport(null);
    scanBufferRef.current = '';
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, []);

  // Mock scan for development testing
  const handleMockScan = useCallback(() => {
    processScan(MOCK_NY_ID_SCAN);
  }, [processScan]);

  const hasSelectedFields = fieldSelections.some((s) => s.selected);
  const someSelected =
    fieldSelections.some((s) => s.selected) &&
    !fieldSelections.every((s) => s.selected);

  const connectActions =
    scannerStatus.state !== 'connected' &&
    (isSerialSupported || isHidSupported) ? (
      <div className="flex flex-col items-center gap-2">
        {isSerialSupported && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleConnectSerialScanner}
            >
              Connect scanner
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Baud rate</span>
              <Select
                value={String(serialBaudRate)}
                onValueChange={(value) => setSerialBaudRate(Number(value))}
              >
                <SelectTrigger className="h-7 w-28 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERIAL_BAUD_RATES.map((rate) => (
                    <SelectItem key={rate} value={String(rate)}>
                      {rate}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {isHidSupported && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleConnectHidScanner}
            className="text-xs underline"
          >
            Connect HID POS scanner
          </Button>
        )}
      </div>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>ID Card information</DialogTitle>
        </DialogHeader>

        {/* Hidden input for scanner */}
        <textarea
          ref={inputRef}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          aria-label="Scanner input"
          className="absolute -left-[9999px] opacity-0 w-px h-px"
        />

        <div className="flex-1 overflow-auto py-4">
          {scanState === 'scanning' && (
            <div className="flex flex-col items-center gap-6 py-16">
              <p className="text-gray-500 text-lg">Scan ID...</p>
              {scannerStatus.state === 'connected' && (
                <p className="text-sm text-gray-500">
                  Scanner connected (
                  {scannerStatus.transport === 'serial' ? 'Serial' : 'HID POS'})
                </p>
              )}
              {scannerStatus.state === 'unsupported' && (
                <p className="text-sm text-amber-600">
                  Scanner is not in HID POS mode — keyboard mode active.
                </p>
              )}
              {connectActions}
              {stage !== 'prod' && stage !== 'staging' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMockScan}
                  className="text-xs"
                >
                  Test Scan (Dev Only)
                </Button>
              )}
            </div>
          )}

          {scanState === 'error' && (
            <div className="flex flex-col items-center gap-4 py-10">
              <p className="text-center text-red-600">
                {parseResult?.error ||
                  'Failed to read ID card. Please try again.'}
              </p>
              {lastDeliveryTransport === 'serial' && (
                <p className="text-center text-sm text-amber-600">
                  If using a serial scanner, verify it is configured for{' '}
                  {serialBaudRate} baud, or pick a different baud rate below.
                </p>
              )}
              <Button variant="outline" onClick={handleRetry}>
                Try Again
              </Button>
              {connectActions}
            </div>
          )}

          {scanState === 'scanned' && fieldSelections.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead className="font-semibold">Fields</TableHead>
                  <TableHead className="font-semibold">
                    Current values
                  </TableHead>
                  <TableHead className="font-semibold">New values</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Select All row */}
                <TableRow
                  className="cursor-pointer"
                  onClick={() => handleSelectAll(!selectAll)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={
                        selectAll
                          ? true
                          : someSelected
                          ? 'indeterminate'
                          : false
                      }
                      onCheckedChange={(checked) =>
                        handleSelectAll(checked === true)
                      }
                      className="border-red-500 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500 data-[state=indeterminate]:bg-red-500 data-[state=indeterminate]:border-red-500"
                    />
                  </TableCell>
                  <TableCell className="font-medium">Select all</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>

                {fieldSelections.map(
                  ({ field, selected, scannedValue, currentValue }) => (
                    <TableRow
                      key={field}
                      className="cursor-pointer"
                      onClick={() => handleFieldToggle(field, !selected)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected}
                          onCheckedChange={(checked) =>
                            handleFieldToggle(field, checked === true)
                          }
                          className="border-red-500 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                        />
                      </TableCell>
                      <TableCell>
                        {CUSTOM_DISPLAY_NAMES[field] ||
                          FIELD_DISPLAY_NAMES[field]}
                      </TableCell>
                      <TableCell className="text-gray-500">
                        {currentValue || '-'}
                      </TableCell>
                      <TableCell>{scannedValue}</TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {scanState === 'scanned' && (
            <Button
              variant="destructive"
              onClick={handleApply}
              disabled={!hasSelectedFields}
            >
              Apply
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScanIDModal;
